import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import {
  Monitor,
  Palette,
  Image,
  Sun,
  Moon,
  Sunset,
  TreePine,
  Waves,
  Mountain,
  Check,
  Sparkles,
  Zap,
  Gamepad2,
  Stars,
  Terminal,
  Type,
  Network,
  Clock,
  HardDrive,
  Snowflake,
  Bird,
  Building,
  Box,
  Computer
} from 'lucide-react';
import { MapleLeaf } from '@/components/common/MapleLeaf';
import { cn } from '../../lib/utils';
import { useToast } from '../../hooks/use-toast';
import { useUserSettings, useUserDesktopConfig } from '../../hooks/useUserSettings';
import { useAuth } from '../../hooks/useAuth';
import { ScreensaverType } from '../Screensaver';
import { MatrixRain } from '../screensavers/MatrixRain';
import { SpaceInvaders } from '../screensavers/SpaceInvaders';
import { Starfield } from '../screensavers/Starfield';
import { BouncingLogo } from '../screensavers/BouncingLogo';
import { RetroTerminal } from '../screensavers/RetroTerminal';
import { FloatingText } from '../screensavers/FloatingText';
import { Pipes } from '../screensavers/Pipes';
import { DigitalClock } from '../screensavers/DigitalClock';
import { DiskDefrag } from '../screensavers/DiskDefrag';
import { getAssetPath } from '../../utils/assetPath';
import WallpaperUploadPanel from './WallpaperUploadPanel';

interface DisplayPropertiesProps {
  onBackgroundChange?: (backgroundUrl: string) => void;
  onThemeChange?: (theme: string) => void;
  onClose?: () => void;
}

export const DisplayProperties: React.FC<DisplayPropertiesProps> = ({
  onBackgroundChange,
  onThemeChange,
  onClose
}) => {
  const { toast } = useToast();
  const { loading: authLoading, user } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Fast Supabase-backed settings
  const { settings, updateSetting, saveSettings, isLoaded } = useUserSettings('display', {
    selectedBackground: 'default',
    selectedAccent: 'red',
    screensaverType: 'bouncing' as ScreensaverType,
    screensaverDuration: 5,
    isDarkMode: true,
    iconTextColor: 'auto',
    customIconTextColor: '255 255 255',
    iconBackgroundOpacity: 0.5,
    // NEW: default desktop color (H S% L% string used by hsl(var(--desktop)))
    desktopColor: '200 25% 92%',
    // Text color on primary/accent surfaces
    primaryForegroundMode: 'auto',
    accentForegroundMode: 'auto',
    primaryForegroundCustom: '220 20% 8%',
    accentForegroundCustom: '220 20% 8%',
    // NEW: allow disabling accent-colored title bars
    useAccentTitlebar: true,
  });
  
  // Save backgroundUrl into user desktop config for reliable persistence
  const { saveDesktopConfig, desktopConfig, isLoaded: desktopConfigLoaded } = useUserDesktopConfig();
  
  const [isPreviewingScreensaver, setIsPreviewingScreensaver] = useState(false);
  const ignoreDomSyncRef = useRef(false);

  // NEW: local state for desktop color
  const [desktopColor, setDesktopColor] = useState<string>('200 25% 92%');

  const [selectedBackground, setSelectedBackground] = useState<string>('default');
  const [selectedAccent, setSelectedAccent] = useState<string>('red');
  const [selectedScreensaver, setSelectedScreensaver] = useState<ScreensaverType>('bouncing');
  const [screensaverDuration, setScreensaverDuration] = useState<number>(5);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [useAccentTitlebar, setUseAccentTitlebar] = useState<boolean>(true);

  type IconTextColorMode = 'auto' | 'light' | 'dark' | 'custom';
  const [iconTextColor, setIconTextColor] = useState<IconTextColorMode>('auto');
  const [customIconTextColor, setCustomIconTextColor] = useState<string>('255 255 255');
  const [iconBackgroundOpacity, setIconBackgroundOpacity] = useState<number>(0.5);

  type SurfaceTextMode = 'auto' | 'light' | 'dark' | 'custom';
  const [primaryForegroundMode, setPrimaryForegroundMode] = useState<SurfaceTextMode>('auto');
  const [accentForegroundMode, setAccentForegroundMode] = useState<SurfaceTextMode>('auto');
  const [primaryForegroundCustom, setPrimaryForegroundCustom] = useState<string>('220 20% 8%');
  const [accentForegroundCustom, setAccentForegroundCustom] = useState<string>('220 20% 8%');

  const [hasInitialized, setHasInitialized] = useState(false);

  // Debounce timer for screensaver duration saves
  const durationSaveTimer = useRef<number | null>(null);

  const clampSurfaceTextMode = (v: unknown): SurfaceTextMode =>
    v === 'auto' || v === 'light' || v === 'dark' || v === 'custom' ? v : 'auto';

  const parseHslLightness = (hsl: string): number | null => {
    const parts = (hsl || '').trim().split(/\s+/);
    if (parts.length < 3) return null;
    const lRaw = parts[2].replace('%', '');
    const l = Number.parseFloat(lRaw);
    return Number.isFinite(l) ? l : null;
  };

  const parseHsl = (hsl: string): { h: number; s: number; l: number } | null => {
    const parts = (hsl || '').trim().split(/\s+/);
    if (parts.length < 3) return null;
    const h = Number.parseFloat(parts[0]);
    const s = Number.parseFloat(parts[1].replace('%', '')) / 100;
    const l = Number.parseFloat(parts[2].replace('%', '')) / 100;
    if (![h, s, l].every(Number.isFinite)) return null;
    return { h: ((h % 360) + 360) % 360, s: Math.max(0, Math.min(1, s)), l: Math.max(0, Math.min(1, l)) };
  };

  const hslToRgb = (hsl: string): { r: number; g: number; b: number } | null => {
    const p = parseHsl(hsl);
    if (!p) return null;
    const { h, s, l } = p;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let r1 = 0;
    let g1 = 0;
    let b1 = 0;

    if (h < 60) {
      r1 = c;
      g1 = x;
    } else if (h < 120) {
      r1 = x;
      g1 = c;
    } else if (h < 180) {
      g1 = c;
      b1 = x;
    } else if (h < 240) {
      g1 = x;
      b1 = c;
    } else if (h < 300) {
      r1 = x;
      b1 = c;
    } else {
      r1 = c;
      b1 = x;
    }

    return {
      r: Math.max(0, Math.min(1, r1 + m)),
      g: Math.max(0, Math.min(1, g1 + m)),
      b: Math.max(0, Math.min(1, b1 + m)),
    };
  };

  const relativeLuminance = (hsl: string): number | null => {
    const rgb = hslToRgb(hsl);
    if (!rgb) return null;

    const lin = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    const r = lin(rgb.r);
    const g = lin(rgb.g);
    const b = lin(rgb.b);

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const pickForegroundForBg = (
    bgHsl: string,
    mode: SurfaceTextMode,
    customHsl: string
  ): string => {
    const DARK_TEXT = '220 20% 8%';
    const LIGHT_TEXT = '0 0% 100%';

    if (mode === 'light') return LIGHT_TEXT;
    if (mode === 'dark') return DARK_TEXT;
    if (mode === 'custom') return customHsl;

    // Auto: use perceptual luminance (HSL lightness is misleading for bright lime/yellow hues)
    const lum = relativeLuminance(bgHsl);
    if (lum === null) return LIGHT_TEXT;
    return lum >= 0.58 ? DARK_TEXT : LIGHT_TEXT;
  };

  const handlePrimaryTextModeChange = (mode: SurfaceTextMode) => {
    setPrimaryForegroundMode(mode);
    updateSetting('primaryForegroundMode', mode);
    applyAccentColors(undefined, undefined, { primary: mode });
  };

  const handleAccentTextModeChange = (mode: SurfaceTextMode) => {
    setAccentForegroundMode(mode);
    updateSetting('accentForegroundMode', mode);
    applyAccentColors(undefined, undefined, { accent: mode });
  };

  const handlePrimaryTextCustomChange = (hex: string) => {
    const hsl = hexToHslString(hex);
    setPrimaryForegroundCustom(hsl);
    updateSetting('primaryForegroundCustom', hsl);
    applyAccentColors(undefined, undefined, { primary: 'custom' });
  };

  const handleAccentTextCustomChange = (hex: string) => {
    const hsl = hexToHslString(hex);
    setAccentForegroundCustom(hsl);
    updateSetting('accentForegroundCustom', hsl);
    applyAccentColors(undefined, undefined, { accent: 'custom' });
  };

  // Helper: normalize URL to its pathname for reliable comparison
  const normalizeUrl = (u: string) => {
    try {
      const full = new URL(u, window.location.origin);
      return full.pathname;
    } catch {
      return u || '';
    }
  };

  // Helper: get file name from a URL or path
  const getFileName = (u: string) => {
    try {
      const full = new URL(u, window.location.origin);
      const parts = full.pathname.split('/');
      return parts[parts.length - 1] || '';
    } catch {
      const parts = (u || '').split('/');
      return parts[parts.length - 1] || '';
    }
  };

  // Helper: resolve wallpaper id from a URL using path OR filename
  const resolveWallpaperIdFromUrl = (u: string | null) => {
    if (!u) return null;
    const normEffective = normalizeUrl(u);
    const effectiveFile = getFileName(u);

    // Try exact path match first
    let match = wallpapers.find(w => normalizeUrl(w.url) === normEffective);
    if (match) return match.id;

    // Fallback: match by filename only
    match = wallpapers.find(w => getFileName(w.url) === effectiveFile);
    return match ? match.id : null;
  };

  // Helper: read the current desktop background URL from the DOM
  const getDomBackgroundUrl = (): string | null => {
    const el = document.querySelector('.desktop-background') as HTMLElement | null;
    if (!el) return null;
    const bg = getComputedStyle(el).backgroundImage; // e.g., url("...") or none
    if (!bg || bg === 'none') return null;
    const match = bg.match(/url\(["']?(.*?)["']?\)/);
    return match ? match[1] : null;
  };

  // Helper to scope to the actual MapleOS root for this window.
  // Some portaled UI elements also use the mapleos-root class, so a global
  // document.querySelector can target the wrong element and leave the real
  // desktop theme variables unchanged.
  const getMapleRoot = () => {
    const scopedRoot = containerRef.current?.closest('.mapleos-root') as HTMLElement | null;
    return scopedRoot ?? (document.querySelector('.mapleos-root') as HTMLElement) ?? document.documentElement;
  };

  // Set CSS variables on both the scoped MapleOS root and documentElement to avoid scope mismatches
  const setScopedVar = (name: string, value: string) => {
    const root = getMapleRoot();
    root.style.setProperty(name, value);
    document.documentElement.style.setProperty(name, value);
  };

  // Initialize local state only once when settings are loaded
  useEffect(() => {
    console.log('DisplayProperties: Settings loaded:', { isLoaded, settings, hasInitialized });
    if (authLoading) return;

    if (isLoaded && settings && !hasInitialized) {
      console.log('DisplayProperties: Initializing with loaded settings:', settings);

      // wallpaper highlight is handled elsewhere
      const loadedAccentId = (settings.selectedAccent as string) || 'red';
      const safeAccentId = accentColors.some(a => a.id === loadedAccentId) ? loadedAccentId : 'red';
      setSelectedAccent(safeAccentId);
      setSelectedScreensaver(settings.screensaverType || 'bouncing');

      // Validate duration so Slider doesn't clamp to min (1)
      const loadedDuration = Number(settings.screensaverDuration);
      const safeDuration =
        Number.isFinite(loadedDuration) && loadedDuration >= 1 && loadedDuration <= 60
          ? loadedDuration
          : 5;
      setScreensaverDuration(safeDuration);

      // Read current DOM theme; do NOT flip it on open
      const root = getMapleRoot();
      const domDark = root.classList.contains('dark');
      setIsDarkMode(domDark);

      // Icon appearance state
      const initIconMode: IconTextColorMode =
        settings.iconTextColor === 'auto' ||
        settings.iconTextColor === 'light' ||
        settings.iconTextColor === 'dark' ||
        settings.iconTextColor === 'custom'
          ? settings.iconTextColor
          : 'auto';
      const initCustomRGB = settings.customIconTextColor ?? '255 255 255';
      const initBgOpacity =
        typeof settings.iconBackgroundOpacity === 'number'
          ? settings.iconBackgroundOpacity
          : 0.5;

      setIconTextColor(initIconMode);
      setCustomIconTextColor(initCustomRGB);
      setIconBackgroundOpacity(initBgOpacity);

      // ✅ Apply icon styles immediately with saved values (no stale state)
      applyIconStyles(initIconMode, initCustomRGB, initBgOpacity);

      // NEW: Surface text modes
      const initPrimaryFg = clampSurfaceTextMode((settings as any).primaryForegroundMode);
      const initAccentFg = clampSurfaceTextMode((settings as any).accentForegroundMode);
      setPrimaryForegroundMode(initPrimaryFg);
      setAccentForegroundMode(initAccentFg);

      const initPrimaryCustom = (settings as any).primaryForegroundCustom || '220 20% 8%';
      const initAccentCustom = (settings as any).accentForegroundCustom || '220 20% 8%';
      setPrimaryForegroundCustom(initPrimaryCustom);
      setAccentForegroundCustom(initAccentCustom);

      // NEW: Desktop color
      const loadedDesktopColor = (settings.desktopColor as string) || '200 25% 92%';
      setDesktopColor(loadedDesktopColor);
      // Apply immediately
      setScopedVar('--desktop', loadedDesktopColor);

      // NEW: titlebar color toggle
      const initUseAccentTitlebar =
        typeof (settings as any).useAccentTitlebar === 'boolean'
          ? Boolean((settings as any).useAccentTitlebar)
          : true;
      setUseAccentTitlebar(initUseAccentTitlebar);

      setHasInitialized(true);

      // Apply accent colors based on actual DOM mode (includes text foregrounds)
      const savedAccent = accentColors.find(a => a.id === safeAccentId);
      if (savedAccent) {
        applyAccentColors(
          savedAccent,
          root.classList.contains('dark') ? 'dark' : 'light',
          undefined,
          initUseAccentTitlebar
        );
      }
    }
  }, [authLoading, isLoaded, settings, hasInitialized, onBackgroundChange, desktopConfigLoaded, desktopConfig?.backgroundUrl]);

  // ---- backfill backgroundId once if it's missing (supports older rows that only stored URL) ----
  useEffect(() => {
    if (!desktopConfigLoaded) return;
    if (!desktopConfig?.backgroundId) {
      const inferred =
        resolveWallpaperIdFromUrl(
          (desktopConfig?.backgroundUrl as string | undefined) ?? getDomBackgroundUrl()
        ) ||
        (settings?.selectedBackground as string | undefined) ||
        'default';

      if (inferred) {
        saveDesktopConfig('backgroundId', inferred);
      }
    }
  }, [
    desktopConfigLoaded,
    desktopConfig?.backgroundId,
    desktopConfig?.backgroundUrl,
    settings?.selectedBackground,
    saveDesktopConfig
  ]);

  useEffect(() => {
    if (!hasInitialized) return;
    // always recompute using the latest state (but only after loaded settings are applied)
    applyAccentColors();
  }, [isDarkMode, selectedAccent, useAccentTitlebar, hasInitialized]);
  

  useEffect(() => {
    const id = pickEffectiveBackgroundId();
    setSelectedBackground(prev => (prev !== id ? id : prev));
  }, [
    desktopConfigLoaded,
    desktopConfig?.backgroundId,   // new: watch the authoritative ID
    desktopConfig?.backgroundUrl,  // fallback: URL changes
    isLoaded,
    settings?.selectedBackground   // last-resort fallback
  ]);

  // NEW: observe desktop background changes and keep highlight in sync (with functional update)
  useEffect(() => {
    const el = document.querySelector('.desktop-background') as HTMLElement | null;
    if (!el) return;

    const updateFromDom = () => {
      if (ignoreDomSyncRef.current) return;
      const domUrl = getDomBackgroundUrl();
      const id = resolveWallpaperIdFromUrl(domUrl);
      setSelectedBackground(prev => (id && prev !== id ? id : prev));
    };

    const t = setTimeout(updateFromDom, 50);
    const observer = new MutationObserver(updateFromDom);
    observer.observe(el, { attributes: true, attributeFilter: ['style'] });

    return () => { clearTimeout(t); observer.disconnect(); };
  }, []);

  // NEW: keep selectedScreensaver label in sync if saved settings change after load
  // NEW: keep screensaverDuration in sync if settings load/change after first render
  useEffect(() => {
    if (!isLoaded) return;

    const raw = (settings?.screensaverDuration as unknown) as number | string | undefined;
    const loaded = Number(raw);
    const safe =
      Number.isFinite(loaded) && loaded >= 1 && loaded <= 60 ? loaded : 5;

    if (safe !== screensaverDuration) {
      setScreensaverDuration(safe);
    }
  }, [isLoaded, settings?.screensaverDuration]);  // <= important dep


  useEffect(() => {
    if (isLoaded && settings?.screensaverType) {
      setSelectedScreensaver(settings.screensaverType as ScreensaverType);
    }
  }, [isLoaded, settings?.screensaverType]);

  // Helpers for color conversion
  const hexToHslString = (hex: string): string => {
    const h = hex.replace('#','');
    const r = parseInt(h.substring(0,2),16)/255;
    const g = parseInt(h.substring(2,4),16)/255;
    const b = parseInt(h.substring(4,6),16)/255;
    const max = Math.max(r,g,b), min = Math.min(r,g,b);
    let hDeg = 0, s = 0, l = (max + min) / 2;
    const d = max - min;
    if (d !== 0) {
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch(max){
        case r: hDeg = (g - b) / d + (g < b ? 6 : 0); break;
        case g: hDeg = (b - r) / d + 2; break;
        case b: hDeg = (r - g) / d + 4; break;
      }
      hDeg = hDeg * 60;
    }
    const sPct = Math.round(s * 100);
    const lPct = Math.round(l * 100);
    return `${Math.round(hDeg)} ${sPct}% ${lPct}%`;
  };

  const hslStringToHex = (hsl: string): string => {
    // hsl like "200 25% 92%"
    try {
      const parts = hsl.trim().split(/\s+/);
      let h = parseFloat(parts[0]);
      let s = parseFloat(parts[1].replace('%',''))/100;
      let l = parseFloat(parts[2].replace('%',''))/100;
      const c = (1 - Math.abs(2*l - 1)) * s;
      const x = c * (1 - Math.abs(((h/60) % 2) - 1));
      const m = l - c/2;
      let r1=0, g1=0, b1=0;
      if (0 <= h && h < 60) { r1=c; g1=x; b1=0; }
      else if (60 <= h && h < 120) { r1=x; g1=c; b1=0; }
      else if (120 <= h && h < 180) { r1=0; g1=c; b1=x; }
      else if (180 <= h && h < 240) { r1=0; g1=x; b1=c; }
      else if (240 <= h && h < 300) { r1=x; g1=0; b1=c; }
      else { r1=c; g1=0; b1=x; }
      const r = Math.round((r1 + m) * 255);
      const g = Math.round((g1 + m) * 255);
      const b = Math.round((b1 + m) * 255);
      const toHex = (v:number) => v.toString(16).padStart(2,'0');
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    } catch {
      return '#dfe6ee';
    }
  };

  // Reconcile desktop color if DB changes later
  useEffect(() => {
    if (!isLoaded) return;
    const dbDesktopColor = (settings?.desktopColor as string) || '200 25% 92%';
    if (dbDesktopColor !== desktopColor) {
      setDesktopColor(dbDesktopColor);
      setScopedVar('--desktop', dbDesktopColor);
    }
  }, [isLoaded, settings?.desktopColor, desktopColor]);

  // ADD: handleDesktopColorHexChange to convert HEX -> HSL, update state, settings, CSS var, and desktop config
  const handleDesktopColorHexChange = (hex: string) => {
    const hsl = hexToHslString(hex);
    setDesktopColor(hsl);
    updateSetting('desktopColor', hsl);
    setScopedVar('--desktop', hsl);
    saveDesktopConfig('desktopColor', hsl);
    toast({ title: 'Desktop Color Updated', description: 'Solid desktop color applied.' });
  };

  type WallpaperOption = {
    id: string;
    name: string;
    url: string;
    preview: string;
    icon: React.ComponentType<any>;
    isCustom?: boolean;
  };

  // Predefined wallpaper options (Solid Color moved to last)
  const presetWallpapers: WallpaperOption[] = [
    {
      id: 'default',
      name: 'Classic Maple',
      url: '/os/assets/images/wallpapers/drops_maple_leaf_autumn_1319773_1600x900.jpg',
      preview: '/os/assets/images/wallpapers/drops_maple_leaf_autumn_1319773_1600x900.jpg',
      icon: MapleLeaf
    },
    {
      id: 'edmonton',
      name: 'Edmonton Nights',
      url: '/os/assets/images/wallpapers/edmonton_canada_night_city_118462_1600x900.jpg',
      preview: '/os/assets/images/wallpapers/edmonton_canada_night_city_118462_1600x900.jpg',
      icon: Snowflake
    },
    {
      id: 'owl',
      name: 'Snow Owl',
      url: '/os/assets/images/wallpapers/owl_white_owl_polar_owl_136287_1600x900.jpg',
      preview: '/os/assets/images/wallpapers/owl_white_owl_polar_owl_136287_1600x900.jpg',
      icon: Bird
    },
    {
      id: 'toronto',
      name: 'Toronto Sky',
      url: '/os/assets/images/wallpapers/city_buildings_architecture_160579_1600x900.jpg',
      preview: '/os/assets/images/wallpapers/city_buildings_architecture_160579_1600x900.jpg',
      icon: Building
    },
    {
      id: 'deer',
      name: 'Deer',
      url: '/os/assets/images/wallpapers/deer_wildlife_horns_131368_1600x900.jpg',
      preview: '/os/assets/images/wallpapers/deer_wildlife_horns_131368_1600x900.jpg',
      icon: Sunset
    },
    {
      id: 'cubes',
      name: '3D Cubes',
      url: '/os/assets/images/wallpapers/cubes_edges_abstraction_1312232_1600x900.jpg',
      preview: '/os/assets/images/wallpapers/cubes_edges_abstraction_1312232_1600x900.jpg',
      icon: Box
    },
    {
      id: 'none',
      name: 'Solid Color (No Wallpaper)',
      url: '',
      preview: '',
      icon: Palette
    }
  ];

  const customWallpaperUrl =
    typeof desktopConfig?.customWallpaperUrl === 'string' ? desktopConfig.customWallpaperUrl : '';
  const customWallpaperName =
    typeof desktopConfig?.customWallpaperName === 'string' && desktopConfig.customWallpaperName.trim()
      ? desktopConfig.customWallpaperName
      : 'My Wallpaper';

  const wallpapers: WallpaperOption[] = [
    ...(customWallpaperUrl
      ? [{
          id: 'custom',
          name: customWallpaperName,
          url: customWallpaperUrl,
          preview: customWallpaperUrl,
          icon: Image,
          isCustom: true,
        } satisfies WallpaperOption]
      : []),
    ...presetWallpapers,
  ];

  // Accent color options
  type AccentOption = {
    id: string;
    name: string;
    colors: {
      light: { primary: string; accent: string };
      dark: { primary: string; accent: string };
    };
    preview: string;
  };

  const standardAccentColors: AccentOption[] = [
    {
      id: 'blue',
      name: 'Blue',
      colors: {
        light: {
          primary: '210 85% 45%',
          accent: '210 50% 85%'
        },
        dark: {
          primary: '210 85% 55%',
          accent: '210 75% 25%'
        }
      },
      preview: 'from-blue-500 to-blue-600'
    },
    {
      id: 'purple',
      name: 'Purple',
      colors: {
        light: {
          primary: '270 90% 50%',
          accent: '270 50% 85%'
        },
        dark: {
          primary: '270 85% 60%',
          accent: '270 75% 25%'
        }
      },
      preview: 'from-purple-500 to-purple-600'
    },
    {
      id: 'green',
      name: 'Green',
      colors: {
        light: {
          primary: '160 84% 39%',
          accent: '160 50% 85%'
        },
        dark: {
          primary: '160 84% 49%',
          accent: '160 75% 25%'
        }
      },
      preview: 'from-green-500 to-green-600'
    },
    {
      id: 'orange',
      name: 'Orange',
      colors: {
        light: {
          primary: '20 90% 50%',
          accent: '20 50% 85%'
        },
        dark: {
          primary: '20 90% 60%',
          accent: '20 75% 25%'
        }
      },
      preview: 'from-orange-500 to-orange-600'
    },
    {
      id: 'red',
      name: 'Red',
      colors: {
        light: {
          primary: '0 84% 60%',
          accent: '0 50% 85%'
        },
        dark: {
          primary: '0 84% 65%',
          accent: '0 75% 25%'
        }
      },
      preview: 'from-red-500 to-red-600'
    },
    {
      id: 'teal',
      name: 'Teal',
      colors: {
        light: {
          primary: '180 84% 39%',
          accent: '180 50% 85%'
        },
        dark: {
          primary: '180 84% 49%',
          accent: '180 75% 25%'
        }
      },
      preview: 'from-teal-500 to-teal-600'
    }
  ];

  const neonAccentColors: AccentOption[] = [
    // Includes MapleOS website accent
    {
      id: 'neon-lime',
      name: 'Neon Lime',
      colors: {
        light: { primary: '75 100% 42%', accent: '75 80% 90%' },
        dark: { primary: '75 100% 55%', accent: '75 90% 22%' }
      },
      preview: 'from-lime-400 to-lime-500'
    },
    {
      id: 'neon-cyan',
      name: 'Neon Cyan',
      colors: {
        light: { primary: '190 100% 42%', accent: '190 85% 90%' },
        dark: { primary: '190 100% 58%', accent: '190 90% 22%' }
      },
      preview: 'from-cyan-400 to-cyan-500'
    },
    {
      id: 'neon-magenta',
      name: 'Neon Magenta',
      colors: {
        light: { primary: '320 100% 52%', accent: '320 90% 92%' },
        dark: { primary: '320 100% 66%', accent: '320 85% 24%' }
      },
      preview: 'from-fuchsia-400 to-fuchsia-600'
    },
    {
      id: 'neon-violet',
      name: 'Neon Violet',
      colors: {
        light: { primary: '270 100% 55%', accent: '270 90% 92%' },
        dark: { primary: '270 100% 70%', accent: '270 85% 24%' }
      },
      preview: 'from-violet-500 to-purple-600'
    },
    {
      id: 'neon-blue',
      name: 'Neon Blue',
      colors: {
        light: { primary: '210 100% 52%', accent: '210 95% 92%' },
        dark: { primary: '210 100% 68%', accent: '210 90% 24%' }
      },
      preview: 'from-sky-500 to-blue-600'
    },
    {
      id: 'neon-amber',
      name: 'Neon Amber',
      colors: {
        light: { primary: '45 100% 50%', accent: '45 95% 90%' },
        dark: { primary: '45 100% 62%', accent: '45 90% 22%' }
      },
      preview: 'from-amber-400 to-orange-500'
    }
  ];

  const accentColors: AccentOption[] = [...standardAccentColors, ...neonAccentColors];

  // Avoid a brief "red" selection flash by inferring the current accent from CSS vars
  // before the browser paints the window.
  useLayoutEffect(() => {
    if (hasInitialized) return;

    const root = getMapleRoot();
    const mode = root.classList.contains('dark') ? 'dark' : 'light';

    const readVar = (name: string) => {
      const inline = root.style.getPropertyValue(name);
      if (inline && inline.trim()) return inline.trim();
      return getComputedStyle(root).getPropertyValue(name).trim();
    };

    const primary = readVar('--primary');
    if (!primary) return;

    const match = accentColors.find(a => a.colors[mode].primary.trim() === primary);
    if (match && match.id !== selectedAccent) {
      setSelectedAccent(match.id);
    }
  }, [hasInitialized]);

  // Screensaver options
  const screensavers = [
    {
      id: 'none' as ScreensaverType,
      name: 'None',
      description: 'No screensaver - screen stays active',
      icon: Monitor,
      preview: 'Screensaver is disabled and will not activate'
    },
    {
      id: 'bouncing' as ScreensaverType,
      name: 'Bouncing Logo',
      description: 'Classic DVD-style bouncing logo',
      icon: Monitor,
      preview: 'A colorful MapleOS logo that bounces around the screen'
    },
    {
      id: 'matrix' as ScreensaverType,
      name: 'Matrix Rain',
      description: 'Digital rain effect from The Matrix',
      icon: Zap,
      preview: 'Green digital characters falling like rain'
    },
    {
      id: 'invaders' as ScreensaverType,
      name: 'Space Invaders',
      description: 'Playable retro space shooter game',
      icon: Gamepad2,
      preview: 'Interactive space invaders game you can actually play'
    },
    {
      id: 'starfield' as ScreensaverType,
      name: 'Hyperspace',
      description: 'Journey through hyperspace starfield',
      icon: Stars,
      preview: 'Stars rushing past as you travel through space (now slower for better viewing)'
    },
    {
      id: 'terminal' as ScreensaverType,
      name: 'Retro Terminal',
      description: 'Simulated computer terminal with typing',
      icon: Terminal,
      preview: 'Classic green terminal with simulated commands'
    },
    {
      id: 'floating-text' as ScreensaverType,
      name: 'Floating Text',
      description: 'Elegant floating corporate text',
      icon: Type,
      preview: 'Professional MapleOS branding floating gracefully across the screen'
    },
    {
      id: 'pipes' as ScreensaverType,
      name: 'Digital Pipes',
      description: 'Colorful growing pipe network',
      icon: Network,
      preview: 'Classic Windows-style pipes with a modern digital twist'
    },
    {
      id: 'digital-clock' as ScreensaverType,
      name: 'Corporate Clock',
      description: 'Professional digital clock display',
      icon: Clock,
      preview: 'Clean, professional time display with subtle animations'
    },
    {
      id: 'disk-defrag' as ScreensaverType,
      name: 'Disk Defragmenter',
      description: 'Nostalgic disk defragmentation visualization',
      icon: HardDrive,
      preview: 'Watch sectors being analyzed and defragmented in real-time'
    }
  ];

  const pickEffectiveBackgroundId = () => {
    // 1) desktop-config authoritative id
    if (desktopConfig?.backgroundId) return desktopConfig.backgroundId as string;

    // 2) try to map from a url (desktopConfig or DOM)
    const effectiveUrl =
      (desktopConfig?.backgroundUrl as string | undefined) || getDomBackgroundUrl();
    const idFromUrl = resolveWallpaperIdFromUrl(effectiveUrl);
    if (idFromUrl) return idFromUrl;

    // 3) fallback to saved settings
    if (settings?.selectedBackground) return settings.selectedBackground as string;

    return 'default';
  };

  const handleBackgroundSelect = (wallpaper: typeof wallpapers[0]) => {
    ignoreDomSyncRef.current = true;
    setTimeout(() => { ignoreDomSyncRef.current = false; }, 300);

    setSelectedBackground(wallpaper.id);
    updateSetting('selectedBackground', wallpaper.id);

    if (wallpaper.id === 'none') {
      // Clear wallpaper and persist desktop color
      saveDesktopConfig('backgroundId', 'none');
      saveDesktopConfig('backgroundUrl', '');
      onBackgroundChange?.('');
      toast({ title: "Solid Color Enabled", description: "Desktop wallpaper disabled; using your chosen color." });
      return;
    }

    // persist BOTH for robust reopen
    saveDesktopConfig('backgroundId', wallpaper.id);
    saveDesktopConfig('backgroundUrl', wallpaper.url);

    onBackgroundChange?.(wallpaper.url);
    toast({
      title: "Background Changed",
      description: `Desktop background set to "${wallpaper.name}"`,
    });
  };

  const handleCustomWallpaperUploaded = async (wallpaper: {
    name: string;
    path: string;
    url: string;
  }) => {
    ignoreDomSyncRef.current = true;
    setTimeout(() => {
      ignoreDomSyncRef.current = false;
    }, 300);

    setSelectedBackground('custom');
    updateSetting('selectedBackground', 'custom');

    await Promise.all([
      saveDesktopConfig('customWallpaperName', wallpaper.name),
      saveDesktopConfig('customWallpaperPath', wallpaper.path),
      saveDesktopConfig('customWallpaperUrl', wallpaper.url),
      saveDesktopConfig('backgroundId', 'custom'),
      saveDesktopConfig('backgroundUrl', wallpaper.url),
    ]);

    onBackgroundChange?.(wallpaper.url);
    toast({
      title: 'Wallpaper Uploaded',
      description: `Desktop background set to "${wallpaper.name}"`,
    });
  };

  const handleDarkModeToggle = (checked: boolean) => {
    setIsDarkMode(checked);
    updateSetting('isDarkMode', checked);

    const root = getMapleRoot();
    if (checked) root.classList.add('dark'); else root.classList.remove('dark');

    // use the explicit mode to avoid reading stale state
    applyAccentColors(undefined, checked ? 'dark' : 'light');

    toast({
      title: checked ? "Dark Mode Enabled" : "Light Mode Enabled",
      description: `Switched to ${checked ? 'dark' : 'light'} mode`,
    });
  };

  const handleAccentColorSelect = (accent: AccentOption) => {
    console.log('Applying accent color:', accent.name, accent.colors);
    setSelectedAccent(accent.id);
    updateSetting('selectedAccent', accent.id);

    applyAccentColors(accent);

    toast({
      title: "Accent Color Changed",
      description: `Accent color changed to ${accent.name}`,
    });
  };

  const applyAccentColors = (
    accent?: AccentOption,
    forceMode?: 'light' | 'dark',
    foregroundOverrides?: { primary?: SurfaceTextMode; accent?: SurfaceTextMode },
    titlebarOverride?: boolean
  ) => {
    const currentAccent = accent || accentColors.find(a => a.id === selectedAccent);
    if (!currentAccent) return;

    const mode = forceMode ?? (isDarkMode ? 'dark' : 'light');
    const colors = currentAccent.colors[mode];

    setScopedVar('--primary', colors.primary);
    setScopedVar('--accent', colors.accent);
    setScopedVar('--ring', colors.primary);

    // Compute text colors for better contrast
    const effectivePrimaryMode = foregroundOverrides?.primary ?? primaryForegroundMode;
    const effectiveAccentMode = foregroundOverrides?.accent ?? accentForegroundMode;
    const primaryFg = pickForegroundForBg(colors.primary, effectivePrimaryMode, primaryForegroundCustom);
    const accentFg = pickForegroundForBg(colors.accent, effectiveAccentMode, accentForegroundCustom);
    setScopedVar('--primary-foreground', primaryFg);
    setScopedVar('--accent-foreground', accentFg);

    const effectiveUseAccentTitlebar = titlebarOverride ?? useAccentTitlebar;

    // Titlebar styling
    if (effectiveUseAccentTitlebar) {
      setScopedVar('--window-header', colors.primary);
      setScopedVar('--window-header-foreground', primaryFg);

      const primaryHsl = `hsl(${colors.primary})`;
      const darkerPrimary = colors.primary.replace(/(\d+)%\)$/, (_m, l) => {
        const nl = Math.max(10, parseInt(l) - 8);
        return `${nl}%)`;
      });
      const darkerPrimaryHsl = `hsl(${darkerPrimary})`;
      setScopedVar('--gradient-window', `linear-gradient(180deg, ${primaryHsl}, ${darkerPrimaryHsl})`);
    } else {
      // Neutral title bars (match window chrome)
      setScopedVar('--window-header', 'var(--card)');
      setScopedVar('--window-header-foreground', 'var(--foreground)');
      setScopedVar('--gradient-window', 'linear-gradient(180deg, hsl(var(--card)), hsl(var(--muted)))');
    }

    // Window controls: adapt to bright headers
    const headerL = effectiveUseAccentTitlebar
      ? (parseHslLightness(colors.primary) ?? 0)
      : (mode === 'dark' ? 12 : 92);
    const brightHeader = headerL >= 60;
    setScopedVar(
      '--window-control-bg',
      brightHeader ? 'rgba(0, 0, 0, 0.10)' : 'rgba(255, 255, 255, 0.12)'
    );
    setScopedVar(
      '--window-control-bg-hover',
      brightHeader ? 'rgba(0, 0, 0, 0.16)' : 'rgba(255, 255, 255, 0.18)'
    );
  };

  const handleUseAccentTitlebarChange = (checked: boolean) => {
    setUseAccentTitlebar(checked);
    updateSetting('useAccentTitlebar', checked);
    // Apply immediately using the incoming value (avoid stale state causing inverted behavior)
    applyAccentColors(undefined, undefined, undefined, checked);
  };

  const handleScreensaverSelect = (screensaver: typeof screensavers[0]) => {
    setSelectedScreensaver(screensaver.id);
    updateSetting('screensaverType', screensaver.id);
    
    toast({
      title: "Screensaver Changed",
      description: `Screensaver set to "${screensaver.name}"`,
    });
  };

  const handleDurationChange = (value: number[]) => {
    const duration = Math.round(value[0]);           // ensure integer
    setScreensaverDuration(duration);

    // Debounced save (no toast)
    if (durationSaveTimer.current) {
      window.clearTimeout(durationSaveTimer.current);
    }
    durationSaveTimer.current = window.setTimeout(() => {
      updateSetting('screensaverDuration', Math.max(1, Math.min(60, duration)));
    }, 500);
  };

  const handlePreviewScreensaver = () => {
    setIsPreviewingScreensaver(true);
  };

  const handleExitPreview = () => {
    setIsPreviewingScreensaver(false);
  };

  const renderScreensaverPreview = () => {
    switch (selectedScreensaver) {
      case 'none':
        return (
          <div className="fixed inset-0 bg-black flex items-center justify-center">
            <div className="text-center text-white">
              <Monitor className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <h2 className="text-2xl font-bold mb-2">No Screensaver</h2>
              <p className="text-lg opacity-80">Screensaver is disabled</p>
            </div>
          </div>
        );
      case 'matrix':
        return <MatrixRain onActivity={handleExitPreview} />;
      case 'invaders':
        return <SpaceInvaders onActivity={handleExitPreview} />;
      case 'starfield':
        return <Starfield onActivity={handleExitPreview} />;
      case 'terminal':
        return <RetroTerminal onActivity={handleExitPreview} />;
      case 'floating-text':
        return <FloatingText onActivity={handleExitPreview} />;
      case 'pipes':
        return <Pipes onActivity={handleExitPreview} />;
      case 'digital-clock':
        return <DigitalClock onActivity={handleExitPreview} />;
      case 'disk-defrag':
        return <DiskDefrag onActivity={handleExitPreview} />;
      case 'bouncing':
      default:
        return <BouncingLogo onActivity={handleExitPreview} />;
    }
  };

  // icon mode selection
  const handleIconTextColorChange = (mode: IconTextColorMode) => {
    setIconTextColor(mode);
    updateSetting('iconTextColor', mode);
    applyIconStyles(mode, customIconTextColor, iconBackgroundOpacity);
    toast({ title: 'Icon Color Changed', description: `Desktop icon text color set to ${mode}` });
  };

  // opacity slider
  const handleIconBackgroundOpacityChange = (opacity: number) => {
    setIconBackgroundOpacity(opacity);
    updateSetting('iconBackgroundOpacity', opacity);
    applyIconStyles(iconTextColor, customIconTextColor, opacity);
    toast({ title: 'Icon Background Updated', description: `Text background opacity set to ${Math.round(opacity * 100)}%` });
  };

  // replace your current applyIconStyles with this:
  const applyIconStyles = (
    colorMode: 'auto' | 'light' | 'dark' | 'custom' = iconTextColor,
    customRGB: string = customIconTextColor,
    bgOpacity: number = iconBackgroundOpacity
  ) => {
    const root = getMapleRoot();

    // Clamp opacity
    const opacity = Math.max(0, Math.min(1, Number(bgOpacity)));

    if (colorMode === 'auto') {
      root.style.removeProperty('--desktop-icon-text-color');
      root.style.removeProperty('--desktop-icon-bg-opacity');
      return;
    }

    let textRGB = '';
    switch (colorMode) {
      case 'light':
        textRGB = '255 255 255';
        break;
      case 'dark':
        textRGB = '0 0 0';
        break;
      case 'custom':
        textRGB = customRGB; // expected format: "r g b"
        break;
    }

    root.style.setProperty('--desktop-icon-text-color', `rgb(${textRGB})`);
    root.style.setProperty('--desktop-icon-bg-opacity', String(opacity));
  };

  // 🔁 Reconcile after DB values arrive (handles reopen + late hydration)
  useEffect(() => {
    if (!isLoaded) return;

    const dbMode = settings?.iconTextColor ?? 'auto';
    const dbRGB  = settings?.customIconTextColor ?? '255 255 255';
    const dbOp   = typeof settings?.iconBackgroundOpacity === 'number'
      ? settings.iconBackgroundOpacity
      : 0.5;

    const changed =
      dbMode !== iconTextColor ||
      dbRGB  !== customIconTextColor ||
      dbOp   !== iconBackgroundOpacity;

    if (changed) {
      setIconTextColor(dbMode);
      setCustomIconTextColor(dbRGB);
      setIconBackgroundOpacity(dbOp);
      applyIconStyles(dbMode, dbRGB, dbOp);
    }
  }, [
    isLoaded,
    settings?.iconTextColor,
    settings?.customIconTextColor,
    settings?.iconBackgroundOpacity,
    iconTextColor,
    customIconTextColor,
    iconBackgroundOpacity,
  ]);


  // --- Persist settings and desktop config together ---
  const handleSaveAndClose = async () => {
    await saveSettings({
      selectedBackground,
      selectedAccent,
      screensaverType: selectedScreensaver,
      screensaverDuration,
      isDarkMode,
      iconTextColor,
      customIconTextColor,
      iconBackgroundOpacity,
      desktopColor,
      primaryForegroundMode,
      accentForegroundMode,
      primaryForegroundCustom,
      accentForegroundCustom,
      useAccentTitlebar,
    });

    // persist desktop snapshot to os_user_desktop
    const sel = wallpapers.find(w => w.id === selectedBackground);
    await Promise.all([
      saveDesktopConfig('backgroundId', selectedBackground),
      sel ? saveDesktopConfig('backgroundUrl', sel.url) : Promise.resolve(),
    ]);

    toast({
      title: "Settings Saved",
      description: "Your display preferences have been saved to your account.",
    });

    setTimeout(() => onClose?.(), 300);
  };

  // --- Reset defaults + persist them ---
  const handleResetToDefaults = async () => {
    setSelectedBackground('default');
    setSelectedAccent('red');
    setSelectedScreensaver('bouncing');
    setScreensaverDuration(5);
    setIsDarkMode(false);
    setUseAccentTitlebar(true);
    setPrimaryForegroundMode('auto');
    setAccentForegroundMode('auto');
    setPrimaryForegroundCustom('220 20% 8%');
    setAccentForegroundCustom('220 20% 8%');

    // Keep variables in both scopes consistent on reset
    setScopedVar('--primary', '210 85% 45%');
    setScopedVar('--accent', '210 75% 88%');
    setScopedVar('--primary-foreground', '0 0% 100%');
    setScopedVar('--accent-foreground', '0 0% 100%');
    setScopedVar('--window-header', '210 85% 45%');
    setScopedVar('--window-header-foreground', '0 0% 100%');
    setScopedVar('--ring', '210 85% 45%');
    setScopedVar('--gradient-window', 'linear-gradient(180deg, hsl(210 85% 48%), hsl(210 85% 42%))');

    await saveSettings({
      selectedBackground: 'default',
      selectedAccent: 'red',
      screensaverType: 'bouncing',
      screensaverDuration: 5,
      isDarkMode: false,
      useAccentTitlebar: true,
      primaryForegroundMode: 'auto',
      accentForegroundMode: 'auto',
      primaryForegroundCustom: '220 20% 8%',
      accentForegroundCustom: '220 20% 8%',
    });

    const defaultUrl =
      '/os/assets/images/wallpapers/drops_maple_leaf_autumn_1319773_1600x900.jpg';
    await Promise.all([
      saveDesktopConfig('backgroundId', 'default'),
      saveDesktopConfig('backgroundUrl', defaultUrl)
    ]);

    const root = getMapleRoot();
    root.classList.remove('dark');
    root.style.setProperty('--primary', '210 85% 45%');
    root.style.setProperty('--accent', '210 75% 88%');
    root.style.setProperty('--primary-foreground', '0 0% 100%');
    root.style.setProperty('--accent-foreground', '0 0% 100%');
    root.style.setProperty('--window-header', '210 85% 45%');
    root.style.setProperty('--window-header-foreground', '0 0% 100%');
    root.style.setProperty('--ring', '210 85% 45%');
    root.style.setProperty(
      '--gradient-window',
      'linear-gradient(180deg, hsl(210 85% 48%), hsl(210 85% 42%))'
    );

    onBackgroundChange?.(defaultUrl);

    toast({
      title: "Settings Reset",
      description: "Display preferences have been reset to defaults.",
    });
  };

  // Show loading state until settings are loaded
  if (!isLoaded) {
    return (
      <div className="h-full flex flex-col bg-gradient-to-br from-background via-background to-background/95">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading your settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full flex flex-col bg-gradient-to-br from-background via-background to-background/95">
      {/* Header with dark/light mode toggle */}
      <div className="border-b border-border/50 p-6 bg-gradient-to-r from-muted/30 to-muted/10 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10 border border-primary/20">
              <Computer className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text">
                Display Properties
              </h1>
              <p className="text-sm text-muted-foreground">
                Customize your desktop appearance and themes
              </p>
            </div>
          </div>
          
          {/* Enhanced Dark Mode Toggle */}
          <div className="flex items-center gap-4 px-6 py-3 rounded-2xl border-2 border-border bg-background/80 backdrop-blur-sm shadow-lg">
            <div className="flex items-center gap-3">
              <Sun className={cn(
                "h-5 w-5 transition-all duration-300", 
                !isDarkMode ? "text-orange-500 scale-110" : "text-muted-foreground/50 scale-90"
              )} />
              <div className="relative">
                <Switch
                  checked={isDarkMode}
                  onCheckedChange={handleDarkModeToggle}
                  className={cn(
                    "data-[state=checked]:bg-slate-700 data-[state=unchecked]:bg-orange-200",
                    "h-7 w-12 shadow-inner border-2",
                    isDarkMode ? "border-slate-600" : "border-orange-300"
                  )}
                />
              </div>
              <Moon className={cn(
                "h-5 w-5 transition-all duration-300", 
                isDarkMode ? "text-blue-400 scale-110" : "text-muted-foreground/50 scale-90"
              )} />
            </div>
            <div className="text-sm font-medium text-foreground/80">
              {isDarkMode ? 'Dark' : 'Light'} Mode
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-6 overflow-auto">
        <Tabs defaultValue="background" className="h-full">
          <TabsList className="grid w-full grid-cols-3 h-12 p-1 bg-muted/30 backdrop-blur-sm border border-border/50">
            <TabsTrigger value="background" className="flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-md">
              <Image size={16} />
              Background
            </TabsTrigger>
            <TabsTrigger value="themes" className="flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-md">
              <Palette size={16} />
              Accent Colors
            </TabsTrigger>
            <TabsTrigger value="screensaver" className="flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-md">
              <Stars size={16} />
              Screensavers
            </TabsTrigger>
          </TabsList>

          <TabsContent value="background" className="mt-8">
            <Card className="border-border/50 bg-card/50 backdrop-blur-sm shadow-lg">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-lg">
                  <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                    <Image size={18} className="text-primary" />
                  </div>
                  Desktop Wallpaper
                </CardTitle>
                <CardDescription className="text-base">
                  Choose a beautiful wallpaper for your desktop
                </CardDescription>
              </CardHeader>
              <CardContent>
                <WallpaperUploadPanel
                  userId={user?.id ?? null}
                  currentWallpaperUrl={customWallpaperUrl || undefined}
                  currentWallpaperName={customWallpaperName}
                  onUploaded={handleCustomWallpaperUploaded}
                />

                <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
                  {wallpapers.map((wallpaper) => {
                    const IconComponent = wallpaper.icon;
                    const isSolid = wallpaper.id === 'none';
                    return (
                      <div
                        key={wallpaper.id}
                        className={cn(
                          "group relative cursor-pointer rounded-xl border-2 overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-xl",
                          selectedBackground === wallpaper.id
                            ? "border-primary ring-4 ring-primary/20 shadow-lg"
                            : "border-border/30 hover:border-primary/50"
                        )}
                        onClick={() => handleBackgroundSelect(wallpaper)}
                      >
                        <div className="aspect-video relative overflow-hidden rounded-lg">
                          {isSolid ? (
                            <div
                              className="w-full h-full transition-transform duration-300 group-hover:scale-105"
                              style={{ backgroundColor: `hsl(${desktopColor})` }}
                            />
                          ) : (
                            <img
                              src={getAssetPath(wallpaper.preview)}
                              alt={wallpaper.name}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                            />
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <IconComponent className="h-10 w-10 text-white drop-shadow-lg" />
                          </div>
                          {selectedBackground === wallpaper.id && (
                            <div className="absolute top-3 right-3 bg-primary text-primary-foreground rounded-full p-2 shadow-lg">
                              <Check size={14} />
                            </div>
                          )}
                        </div>
                        <div className="p-4 bg-gradient-to-t from-card to-card/80">
                          <p className="font-semibold text-sm text-center">{wallpaper.name}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* NEW: Color picker appears when 'Solid Color' is selected */}
                {selectedBackground === 'none' && (
                  <div className="mt-6 p-4 rounded-xl bg-muted/20 border border-border/50">
                    <Label className="text-sm font-medium">Choose Desktop Color</Label>
                    <div className="flex items-center gap-4 mt-3">
                      <input
                        type="color"
                        value={hslStringToHex(desktopColor)}
                        onChange={(e) => handleDesktopColorHexChange(e.target.value)}
                        className="w-12 h-12 rounded-lg border-2 border-border cursor-pointer"
                      />
                      <div className="text-sm text-muted-foreground">
                        Current (HSL): {desktopColor}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="themes" className="mt-8">
            <Card className="border-border/50 bg-card/50 backdrop-blur-sm shadow-lg">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-lg">
                  <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                    <Palette size={18} className="text-primary" />
                  </div>
                  Accent Colors
                </CardTitle>
                <CardDescription className="text-base">
                  Choose your accent color for {isDarkMode ? 'dark' : 'light'} mode
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <div className="mb-3 text-sm font-medium text-muted-foreground">Standard</div>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                      {standardAccentColors.map((accent) => (
                        <div
                          key={accent.id}
                          className={cn(
                            "group relative cursor-pointer rounded-xl border-2 overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-lg",
                            selectedAccent === accent.id
                              ? "border-primary ring-4 ring-primary/20 shadow-lg"
                              : "border-border/30 hover:border-primary/50"
                          )}
                          onClick={() => handleAccentColorSelect(accent)}
                        >
                          <div className="aspect-[3/2] relative overflow-hidden">
                            <div className={cn(
                              "w-full h-full bg-gradient-to-br transition-transform duration-300 group-hover:scale-110",
                              accent.preview
                            )} />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                            {selectedAccent === accent.id && (
                              <div className="absolute top-2 right-2 bg-white/90 text-primary rounded-full p-1.5 shadow-lg">
                                <Check size={12} />
                              </div>
                            )}
                            <div className="absolute bottom-2 left-2 text-white font-semibold text-sm drop-shadow-lg">
                              {accent.name}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="mb-3 text-sm font-medium text-muted-foreground">Futuristic Neon</div>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                      {neonAccentColors.map((accent) => (
                        <div
                          key={accent.id}
                          className={cn(
                            "group relative cursor-pointer rounded-xl border-2 overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-lg",
                            selectedAccent === accent.id
                              ? "border-primary ring-4 ring-primary/20 shadow-lg"
                              : "border-border/30 hover:border-primary/50"
                          )}
                          onClick={() => handleAccentColorSelect(accent)}
                        >
                          <div className="aspect-[3/2] relative overflow-hidden">
                            <div className={cn(
                              "w-full h-full bg-gradient-to-br transition-transform duration-300 group-hover:scale-110",
                              accent.preview
                            )} />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                            {selectedAccent === accent.id && (
                              <div className="absolute top-2 right-2 bg-white/90 text-primary rounded-full p-1.5 shadow-lg">
                                <Check size={12} />
                              </div>
                            )}
                            <div className="absolute bottom-2 left-2 text-white font-semibold text-sm drop-shadow-lg">
                              {accent.name}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Color Preview */}
                <div className="mt-8 p-6 rounded-xl bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/20">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-4 h-4 rounded-full bg-primary border-2 border-primary-foreground" />
                    <span className="font-medium">Preview: {accentColors.find(a => a.id === selectedAccent)?.name} in {isDarkMode ? 'Dark' : 'Light'} Mode</span>
                  </div>

                  <div className="mb-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="rounded-lg border border-border/50 bg-card/30 p-3">
                      <div className="text-xs font-medium text-muted-foreground">Text on Title Bar / Primary</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(['auto', 'light', 'dark', 'custom'] as const).map((m) => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => handlePrimaryTextModeChange(m)}
                            className={cn(
                              'px-3 py-1.5 rounded-md text-xs border transition-colors',
                              primaryForegroundMode === m
                                ? 'border-primary bg-primary/10 text-foreground'
                                : 'border-border/50 hover:bg-accent/20 text-muted-foreground'
                            )}
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                      {primaryForegroundMode === 'custom' && (
                        <div className="mt-3 flex items-center gap-3">
                          <input
                            type="color"
                            value={hslStringToHex(primaryForegroundCustom)}
                            onChange={(e) => handlePrimaryTextCustomChange(e.target.value)}
                            className="h-9 w-10 rounded-md border border-border"
                          />
                          <div className="text-xs text-muted-foreground">{primaryForegroundCustom}</div>
                        </div>
                      )}
                    </div>

                    <div className="rounded-lg border border-border/50 bg-card/30 p-3">
                      <div className="text-xs font-medium text-muted-foreground">Text on Accent</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(['auto', 'light', 'dark', 'custom'] as const).map((m) => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => handleAccentTextModeChange(m)}
                            className={cn(
                              'px-3 py-1.5 rounded-md text-xs border transition-colors',
                              accentForegroundMode === m
                                ? 'border-primary bg-primary/10 text-foreground'
                                : 'border-border/50 hover:bg-accent/20 text-muted-foreground'
                            )}
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                      {accentForegroundMode === 'custom' && (
                        <div className="mt-3 flex items-center gap-3">
                          <input
                            type="color"
                            value={hslStringToHex(accentForegroundCustom)}
                            onChange={(e) => handleAccentTextCustomChange(e.target.value)}
                            className="h-9 w-10 rounded-md border border-border"
                          />
                          <div className="text-xs text-muted-foreground">{accentForegroundCustom}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mb-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="rounded-lg border border-border/50 bg-card/30 p-3">
                      <div className="text-xs font-medium text-muted-foreground">Title Bars</div>
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <div className="text-sm">
                          <div className="font-medium">Use accent color</div>
                          <div className="text-xs text-muted-foreground">Apply the selected accent to window title bars</div>
                        </div>
                        <Switch
                          checked={useAccentTitlebar}
                          onCheckedChange={handleUseAccentTitlebarChange}
                          className={cn(
                            'shadow-sm ring-1 ring-border/60',
                            'data-[state=unchecked]:bg-muted data-[state=unchecked]:hover:bg-muted/80',
                            'data-[state=checked]:bg-primary'
                          )}
                        />
                      </div>
                    </div>

                    <div className="rounded-lg border border-border/50 bg-card/30 p-3">
                      <div className="text-xs font-medium text-muted-foreground">Text Contrast</div>
                      <div className="mt-2 text-xs text-muted-foreground">Tune text for neon accents and bright title bars</div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-1 p-4 rounded-lg bg-primary text-primary-foreground">
                      <p className="font-medium">Primary</p>
                      <p className="text-sm opacity-90">Buttons, links, highlights</p>
                    </div>
                    <div className="flex-1 p-4 rounded-lg bg-accent text-accent-foreground">
                      <p className="font-medium">Accent</p>
                      <p className="text-sm opacity-90">Secondary elements</p>
                    </div>
                  </div>
                </div>
                
                {/* Desktop Icon Customization */}
                <div className="mt-8">
                  <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center gap-3 text-base">
                        <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                          <Type size={16} className="text-primary" />
                        </div>
                        Desktop Icon Appearance
                      </CardTitle>
                      <CardDescription className="text-sm">
                        Customize how desktop icons look on your wallpaper for better visibility
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Icon Text Color Options */}
                      <div className="space-y-3">
                        <Label className="text-sm font-medium">Icon Text Color</Label>
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            { id: 'auto', name: 'Auto', description: 'Follows theme' },
                            { id: 'light', name: 'Light', description: 'White text' },
                            { id: 'dark', name: 'Dark', description: 'Black text' },
                            { id: 'custom', name: 'Custom', description: 'Your choice' }
                          ].map((option) => (
                            <div
                              key={option.id}
                              className={cn(
                                "p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:bg-accent/20",
                                iconTextColor === option.id
                                  ? "border-primary bg-primary/10"
                                  : "border-border/30 hover:border-primary/50"
                              )}
                              onClick={() => handleIconTextColorChange(option.id as IconTextColorMode)}
                            >
                              <div className="font-medium text-sm">{option.name}</div>
                              <div className="text-xs text-muted-foreground">{option.description}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Custom Color Picker */}
                      {iconTextColor === 'custom' && (
                        <div className="space-y-3">
                          <Label className="text-sm font-medium">Custom Text Color</Label>
                          <div className="flex items-center gap-4">
                            <input
                              type="color"
                              value={`#${Math.round(parseInt(customIconTextColor.split(' ')[0])).toString(16).padStart(2, '0')}${Math.round(parseInt(customIconTextColor.split(' ')[1])).toString(16).padStart(2, '0')}${Math.round(parseInt(customIconTextColor.split(' ')[2])).toString(16).padStart(2, '0')}`.slice(1)}
                              onChange={(e) => {
                                const hex = e.target.value.slice(1);
                                const r = parseInt(hex.slice(0, 2), 16);
                                const g = parseInt(hex.slice(2, 4), 16);
                                const b = parseInt(hex.slice(4, 6), 16);
                                const rgb = `${r} ${g} ${b}`;
                                setCustomIconTextColor(rgb);
                                updateSetting('customIconTextColor', rgb);
                                applyIconStyles(iconTextColor, rgb, iconBackgroundOpacity);
                              }}
                              className="w-12 h-12 rounded-lg border-2 border-border cursor-pointer"
                            />
                            <div className="text-sm text-muted-foreground">
                              RGB: {customIconTextColor}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Background Opacity */}
                      <div className="space-y-3">
                        <Label className="text-sm font-medium">
                          Text Background Opacity: {Math.round(iconBackgroundOpacity * 100)}%
                        </Label>
                        <Slider
                          value={[iconBackgroundOpacity]}
                          onValueChange={(value) => handleIconBackgroundOpacityChange(value[0])}
                          max={1}
                          min={0}
                          step={0.1}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Transparent</span>
                          <span>Solid</span>
                        </div>
                      </div>

                      {/* Icon Preview */}
                      <div className="p-4 rounded-lg bg-gradient-to-br from-muted/20 to-muted/10 border border-border/30">
                        <div className="text-sm font-medium mb-3">Preview</div>
                        <div className="flex items-center justify-center">
                          <div className="desktop-icon-preview flex flex-col items-center w-20">
                            <div className="p-3 mb-1">
                              <Monitor size={32} className="desktop-icon-color" />
                            </div>
                            <span className="text-xs text-center leading-tight px-1 rounded desktop-icon-text-color desktop-icon-bg">
                              Sample Icon
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="screensaver" className="mt-8">
            <Card className="border-border/50 bg-card/50 backdrop-blur-sm shadow-lg">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-lg">
                  <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                    <Monitor size={18} className="text-primary" />
                  </div>
                  Screensavers
                </CardTitle>
                <CardDescription className="text-base">
                  Choose your favorite screensaver for when you're away
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {screensavers.map((screensaver) => {
                    const IconComponent = screensaver.icon;
                    return (
                      <div
                        key={screensaver.id}
                        className={cn(
                          "group relative cursor-pointer rounded-xl border-2 overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-xl",
                          selectedScreensaver === screensaver.id
                            ? "border-primary ring-4 ring-primary/20 shadow-lg"
                            : "border-border/30 hover:border-primary/50"
                        )}
                        onClick={() => handleScreensaverSelect(screensaver)}
                      >
                        <div className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                              <IconComponent className="h-8 w-8 text-primary" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg mb-2">{screensaver.name}</h3>
                              <p className="text-sm text-muted-foreground mb-3">{screensaver.description}</p>
                              <p className="text-xs text-muted-foreground">{screensaver.preview}</p>
                            </div>
                            {selectedScreensaver === screensaver.id && (
                              <div className="p-2 bg-primary text-primary-foreground rounded-full">
                                <Check size={16} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Duration Settings */}
                {selectedScreensaver !== 'none' && (
                  <div className="mt-8 p-6 rounded-xl bg-gradient-to-r from-muted/30 to-muted/10 border border-border/50">
                    <div className="flex items-center gap-3 mb-6">
                      <Clock className="h-5 w-5 text-primary" />
                      <span className="font-medium">Screensaver Duration</span>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">
                          Activate after: <span className="font-medium">{screensaverDuration} minute{screensaverDuration !== 1 ? 's' : ''}</span>
                        </Label>
                      </div>
                      <Slider
                        value={[screensaverDuration]}
                        onValueChange={handleDurationChange}
                        max={60}
                        min={1}
                        step={1}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>1 min</span>
                        <span>60 min</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Screensaver Preview */}
                <div className="mt-8 p-6 rounded-xl bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/20">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-4 h-4 rounded-full bg-primary border-2 border-primary-foreground" />
                    <span className="font-medium">
                      Selected: {screensavers.find(s => s.id === selectedScreensaver)?.name}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {screensavers.find(s => s.id === selectedScreensaver)?.preview}
                  </p>
                  <div className="mt-4 text-xs text-muted-foreground">
                    {selectedScreensaver === 'none' 
                      ? '💡 Tip: No screensaver will activate - your screen will stay active'
                      : `💡 Tip: The screensaver will activate automatically after ${screensaverDuration} minute${screensaverDuration !== 1 ? 's' : ''} of inactivity`
                    }
                  </div>
                  
                  {/* Preview Button */}
                  <div className="mt-6 flex justify-center">
                    <Button 
                      onClick={handlePreviewScreensaver}
                      className="px-8 py-3 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <Stars className="mr-2 h-5 w-5" />
                      Preview Screensaver
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Footer */}
      <div className="border-t border-border/50 p-4 bg-gradient-to-r from-muted/20 to-muted/10 backdrop-blur-sm">
        <div className="flex justify-between items-center">
          <div className="text-sm text-muted-foreground">
            ✨ Changes are applied instantly
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="hover:bg-muted/50"
              onClick={handleResetToDefaults}
            >
              Reset to Defaults
            </Button>
            <Button 
              className="bg-primary hover:bg-primary/90 shadow-md"
              onClick={handleSaveAndClose}
            >
              Done
            </Button>
          </div>
        </div>
      </div>

      {/* Screensaver Preview Overlay */}
      {isPreviewingScreensaver && (
        <div className="fixed inset-0 z-[10000]">
          {renderScreensaverPreview()}
          {/* Preview indicator */}
          <div className="absolute top-10 left-1/2 transform -translate-x-1/2 bg-black/80 text-white px-6 py-3 rounded-xl border border-white/20 backdrop-blur-sm">
            <div className="text-center">
              <div className="font-medium">Screensaver Preview</div>
              <div className="text-sm opacity-80 mt-1">Click anywhere or press any key to exit</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};